import { Shield, LogIn } from 'lucide-react';
import {
  colors,
  typography,
  fontWeights,
  radii,
} from '../styles/designTokens';

/**
 * BetaLandingPage — the initial screen for the private beta login flow.
 *
 * Displays:
 *  - "TravelFlow is in private beta" banner
 *  - Informational copy: access is invite-only via email magic link
 *  - "Already have an account? Sign in" link
 *  - Privacy Policy + OpenTable Branding links
 *
 * Props:
 *  @param {() => void} onSignIn — called when the user clicks the sign-in link
 */
const BetaLandingPage = ({ onSignIn }) => {
  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {/* Beta Badge */}
        <div style={styles.betaBadge}>
          <Shield size={16} />
          PRIVATE BETA
        </div>

        {/* Heading */}
        <h1 style={styles.heading}>
          Welcome to TravelFlow
        </h1>

        {/* Private beta banner text */}
        <p style={styles.bannerText}>
          TravelFlow is currently invite-only. Access is granted through a
          personal email invitation.
        </p>

        {/* Informational copy */}
        <p style={styles.infoText}>
          If you received an invite email, open it and use the link inside to
          get started. The link will set up your account automatically — no
          code to enter.
        </p>
        <p style={styles.infoText}>
          Don&apos;t have an invite yet? Reach out to the person who told you
          about TravelFlow to request access.
        </p>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Sign-in link */}
        <p style={styles.signInText}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSignIn}
            style={styles.signInLink}
            aria-label="Sign in to your account"
          >
            <LogIn size={14} style={{ marginRight: '4px' }} />
            Sign in
          </button>
        </p>

        {/* Public links: Privacy Policy + OpenTable Branding compliance page */}
        <p style={styles.privacyLink}>
          <a
            href="/privacy-policy.html"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.publicAnchor}
          >
            Privacy Policy
          </a>
          <span style={styles.publicLinkSeparator} aria-hidden="true">·</span>
          <a
            href="/opentable-branding.html"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.publicAnchor}
            aria-label="OpenTable Branding compliance page (opens in new tab)"
          >
            OpenTable Branding
          </a>
        </p>
      </div>
    </div>
  );
};

/* ── Styles ──────────────────────────────────────────────────── */

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
    boxSizing: 'border-box',
  },

  card: {
    background: colors.surface,
    borderRadius: radii.card,
    padding: '40px 32px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    maxWidth: '440px',
    width: '100%',
    textAlign: 'center',
    boxSizing: 'border-box',
  },

  betaBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #ff6b6b, #ffa500)',
    color: '#ffffff',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: fontWeights.semibold,
    marginBottom: '24px',
  },

  heading: {
    fontSize: '28px',
    fontWeight: fontWeights.bold,
    color: colors.textPrimary,
    margin: '0 0 12px 0',
  },

  bannerText: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    lineHeight: '1.6',
    margin: '0 0 16px 0',
  },

  infoText: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    lineHeight: '1.6',
    margin: '0 0 16px 0',
  },

  divider: {
    height: '1px',
    background: colors.divider,
    margin: '0 0 24px 0',
  },

  signInText: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    margin: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    flexWrap: 'wrap',
  },

  signInLink: {
    background: 'none',
    border: 'none',
    color: '#667eea',
    fontSize: typography.body.fontSize,
    fontWeight: fontWeights.semibold,
    cursor: 'pointer',
    textDecoration: 'none',
    padding: '0',
    display: 'inline-flex',
    alignItems: 'center',
  },

  privacyLink: {
    color: colors.textMuted,
    fontSize: typography.caption.fontSize,
    margin: '16px 0 0 0',
    textAlign: 'center',
  },

  publicAnchor: {
    color: colors.textMuted,
    textDecoration: 'underline',
  },

  publicLinkSeparator: {
    color: colors.textMuted,
    margin: '0 8px',
  },
};

export default BetaLandingPage;
